import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Wallet,
  Key,
  Shield,
  AlertTriangle,
  Eye,
  EyeOff,
  Copy,
  Check,
  RefreshCw,
  Download,
  Plus,
  Search,
  Filter,
  ChevronRight,
  Lock,
  Unlock,
  Server,
  Users,
  Activity,
  Clock,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2
} from 'lucide-react'
import { WalletAdminAPI } from '../../services/adminApi'

export default function WalletManagementPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [masterWallets, setMasterWallets] = useState([])
  const [networks, setNetworks] = useState([])
  const [addresses, setAddresses] = useState([])
  const [summary, setSummary] = useState(null)
  const [auditLogs, setAuditLogs] = useState([])
  const [showInitModal, setShowInitModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [selectedNetwork, setSelectedNetwork] = useState('')
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [newMnemonic, setNewMnemonic] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [exportReason, setExportReason] = useState('')
  const [exportedKey, setExportedKey] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [walletsRes, networksRes, summaryRes, addressesRes] = await Promise.all([
        WalletAdminAPI.getMasterWallets(),
        WalletAdminAPI.getNetworksForSetup(),
        WalletAdminAPI.getExchangeSummary(),
        WalletAdminAPI.getAllDepositAddresses()
      ])

      if (walletsRes.success) setMasterWallets(walletsRes.data || [])
      if (networksRes.success) setNetworks(networksRes.data || [])
      if (summaryRes.success) setSummary(summaryRes.data)
      if (addressesRes.success) setAddresses(addressesRes.data || [])
    } catch (err) {
      console.error('Failed to fetch wallet data:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchAuditLogs = async () => {
    try {
      const res = await WalletAdminAPI.getAuditLog()
      if (res.success) setAuditLogs(res.data?.logs || [])
    } catch (err) {
      console.error('Failed to fetch audit logs:', err)
    }
  }

  const handleInitializeMasterWallet = async () => {
    if (!selectedNetwork) return
    setProcessing(true)
    try {
      const res = await WalletAdminAPI.initializeMasterWallet(selectedNetwork)
      if (res.success) {
        setNewMnemonic(res.data)
        fetchData()
      }
    } catch (err) {
      console.error('Failed to initialize master wallet:', err)
    } finally {
      setProcessing(false)
    }
  }

  const handleExportPrivateKey = async () => {
    if (!selectedAddress || exportReason.length < 10) return
    setProcessing(true)
    try {
      const res = await WalletAdminAPI.exportPrivateKey(selectedAddress.id, exportReason)
      if (res.success) {
        setExportedKey(res.data.privateKey)
      }
    } catch (err) {
      console.error('Failed to export private key:', err)
    } finally {
      setProcessing(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Wallet Management</h1>
          <p className="text-slate-400 mt-1">Centralized HD wallet custody and management</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg hover:bg-slate-600 text-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowInitModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Initialize Network
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700">
        {['overview', 'master-wallets', 'addresses', 'audit-log'].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab)
              if (tab === 'audit-log') fetchAuditLogs()
            }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Server className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-sm text-slate-400">Master Wallets</span>
              </div>
              <p className="text-2xl font-bold text-white">{masterWallets.length}</p>
              <p className="text-xs text-slate-500 mt-1">Networks configured</p>
            </div>

            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-sm text-slate-400">Total Addresses</span>
              </div>
              <p className="text-2xl font-bold text-white">{summary?.totals?.total_addresses || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{summary?.totals?.total_users_with_addresses || 0} users</p>
            </div>

            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-sm text-slate-400">Total Deposited</span>
              </div>
              <p className="text-2xl font-bold text-white">{parseFloat(summary?.totals?.total_deposited || 0).toFixed(4)}</p>
              <p className="text-xs text-slate-500 mt-1">All networks combined</p>
            </div>

            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-violet-400" />
                </div>
                <span className="text-sm text-slate-400">Pending Balance</span>
              </div>
              <p className="text-2xl font-bold text-white">{parseFloat(summary?.totals?.total_pending || 0).toFixed(4)}</p>
              <p className="text-xs text-slate-500 mt-1">Awaiting confirmation</p>
            </div>
          </div>

          {/* Network Summary */}
          <div className="bg-slate-800 rounded-xl border border-slate-700">
            <div className="p-5 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">Network Summary</h2>
            </div>
            <div className="divide-y divide-slate-700">
              {(summary?.byNetwork || []).map((network) => (
                <div key={network.network_code} className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center font-bold text-slate-300">
                      {network.network_code?.slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-medium text-white">{network.network_name}</p>
                      <p className="text-sm text-slate-400">{network.network_code}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-8 text-right">
                    <div>
                      <p className="text-sm text-slate-400">Addresses</p>
                      <p className="font-semibold text-white">{network.total_addresses}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Deposited</p>
                      <p className="font-semibold text-white">{parseFloat(network.total_deposited || 0).toFixed(6)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Pending</p>
                      <p className="font-semibold text-amber-400">{parseFloat(network.pending_balance || 0).toFixed(6)}</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!summary?.byNetwork || summary.byNetwork.length === 0) && (
                <div className="p-8 text-center text-slate-400">
                  No network data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Master Wallets Tab */}
      {activeTab === 'master-wallets' && (
        <div className="space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-400">Security Notice</p>
              <p className="text-sm text-amber-400/80 mt-1">
                Master wallets contain the root keys for all user deposit addresses.
                The mnemonic phrases are encrypted and stored securely. Handle with extreme caution.
              </p>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl border border-slate-700">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Master HD Wallets</h2>
              <button
                onClick={() => setShowInitModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                Add Network
              </button>
            </div>
            <div className="divide-y divide-slate-700">
              {masterWallets.map((wallet) => (
                <div key={wallet.id} className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      wallet.status === 'active' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                    }`}>
                      {wallet.status === 'active' ? (
                        <Shield className="w-6 h-6 text-emerald-400" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{wallet.network_code}</p>
                      <p className="text-sm text-slate-400 font-mono">{wallet.master_public_key?.slice(0, 30)}...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Total Addresses</p>
                      <p className="font-semibold text-white">{wallet.total_addresses || 0}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Total Received</p>
                      <p className="font-semibold text-white">{parseFloat(wallet.total_received || 0).toFixed(6)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      wallet.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {wallet.status}
                    </span>
                  </div>
                </div>
              ))}
              {masterWallets.length === 0 && (
                <div className="p-8 text-center">
                  <Key className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No master wallets configured</p>
                  <p className="text-sm text-slate-500 mt-1">Initialize a network to start accepting deposits</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Addresses Tab */}
      {activeTab === 'addresses' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700">
          <div className="p-5 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">User Deposit Addresses</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search addresses..."
                  className="pl-9 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm w-64 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50 border-b border-slate-700">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-medium text-slate-400 uppercase">User</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-slate-400 uppercase">Network</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-slate-400 uppercase">Address</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-slate-400 uppercase">Received</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-slate-400 uppercase">Pending</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {addresses.map((addr) => (
                  <tr key={addr.id} className="hover:bg-slate-700/50">
                    <td className="px-5 py-4">
                      <p className="font-medium text-white">{addr.first_name} {addr.last_name}</p>
                      <p className="text-sm text-slate-400">{addr.user_email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="px-2 py-1 bg-slate-700 rounded text-sm font-medium text-slate-300">
                        {addr.network_code}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <code className="text-sm text-slate-300">{addr.address?.slice(0, 20)}...{addr.address?.slice(-8)}</code>
                    </td>
                    <td className="px-5 py-4 font-medium text-white">
                      {parseFloat(addr.total_received || 0).toFixed(6)}
                    </td>
                    <td className="px-5 py-4 font-medium text-amber-400">
                      {parseFloat(addr.pending_balance || 0).toFixed(6)}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => {
                          setSelectedAddress(addr)
                          setShowExportModal(true)
                        }}
                        className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm font-medium"
                      >
                        <Key className="w-4 h-4" />
                        Export Key
                      </button>
                    </td>
                  </tr>
                ))}
                {addresses.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-slate-400">
                      No deposit addresses found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit-log' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700">
          <div className="p-5 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Security Audit Log</h2>
          </div>
          <div className="divide-y divide-slate-700">
            {auditLogs.map((log) => (
              <div key={log.id} className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    log.action.includes('EXPORT') ? 'bg-red-500/20' : 'bg-blue-500/20'
                  }`}>
                    {log.action.includes('EXPORT') ? (
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    ) : (
                      <Activity className="w-5 h-5 text-blue-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">{log.action}</p>
                    <p className="text-sm text-slate-400">
                      {log.admin_email || 'Unknown'} - {log.entity_type}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-400">
                    {new Date(log.created_at).toLocaleString()}
                  </p>
                  {log.details && (
                    <p className="text-xs text-slate-500 mt-1">
                      {JSON.stringify(log.details).slice(0, 50)}...
                    </p>
                  )}
                </div>
              </div>
            ))}
            {auditLogs.length === 0 && (
              <div className="p-8 text-center text-slate-400">
                No audit logs found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Initialize Master Wallet Modal */}
      <AnimatePresence>
        {showInitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !newMnemonic && setShowInitModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-slate-800 rounded-2xl w-full max-w-lg shadow-xl border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-xl font-bold text-white">Initialize Master Wallet</h2>
                <p className="text-slate-400 mt-1">Create a new HD wallet for a blockchain network</p>
              </div>

              {!newMnemonic ? (
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Select Network</label>
                    <select
                      value={selectedNetwork}
                      onChange={(e) => setSelectedNetwork(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Choose a network</option>
                      <optgroup label="🧪 Testnets (Recommended for testing)">
                        {networks.filter(n => !n.has_master_wallet && n.is_testnet).map((n) => (
                          <option key={n.id} value={n.code}>🧪 {n.name}</option>
                        ))}
                      </optgroup>
                      <optgroup label="🔴 Mainnets (Real funds)">
                        {networks.filter(n => !n.has_master_wallet && !n.is_testnet).map((n) => (
                          <option key={n.id} value={n.code}>{n.name} ({n.code})</option>
                        ))}
                      </optgroup>
                    </select>
                    {selectedNetwork && networks.find(n => n.code === selectedNetwork)?.is_testnet && (
                      <p className="mt-2 text-sm text-emerald-400 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        Testnet selected - Safe for testing!
                      </p>
                    )}
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-400">Important</p>
                        <p className="text-sm text-amber-400/80 mt-1">
                          A 24-word mnemonic phrase will be generated. You MUST save it securely.
                          It will only be shown ONCE and cannot be recovered.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowInitModal(false)}
                      className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-medium text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleInitializeMasterWallet}
                      disabled={!selectedNetwork || processing}
                      className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium disabled:opacity-50 transition-colors"
                    >
                      {processing ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Generate Wallet'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-emerald-400 mb-2">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Master Wallet Created!</span>
                    </div>
                    <p className="text-sm text-emerald-400/80">Network: {newMnemonic.networkCode}</p>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-400">SAVE THIS MNEMONIC NOW!</p>
                        <p className="text-sm text-red-400/80 mt-1">
                          This will NOT be shown again. Store it in a secure location.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 text-sm">24-Word Mnemonic</span>
                      <button
                        onClick={() => copyToClipboard(newMnemonic.mnemonic)}
                        className="text-slate-400 hover:text-white transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-white font-mono text-sm break-all leading-relaxed">
                      {newMnemonic.mnemonic}
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setShowInitModal(false)
                      setNewMnemonic(null)
                      setSelectedNetwork('')
                    }}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                  >
                    I've Saved the Mnemonic
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Export Private Key Modal */}
      <AnimatePresence>
        {showExportModal && selectedAddress && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              if (!exportedKey) {
                setShowExportModal(false)
                setExportReason('')
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-slate-800 rounded-2xl w-full max-w-lg shadow-xl border border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-xl font-bold text-red-400">Export Private Key</h2>
                <p className="text-slate-400 mt-1">This action is logged and audited</p>
              </div>

              {!exportedKey ? (
                <div className="p-6 space-y-4">
                  <div className="bg-slate-900 rounded-xl p-4">
                    <p className="text-sm text-slate-400">Address</p>
                    <code className="text-sm font-mono text-white">{selectedAddress.address}</code>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Reason for Export (required, min 10 chars)
                    </label>
                    <textarea
                      value={exportReason}
                      onChange={(e) => setExportReason(e.target.value)}
                      placeholder="Explain why you need to export this private key..."
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-xl h-24 resize-none text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-400">Security Warning</p>
                        <p className="text-sm text-red-400/80 mt-1">
                          Exporting a private key gives full control over the funds.
                          This action is permanently logged for security audit.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowExportModal(false)
                        setExportReason('')
                        setSelectedAddress(null)
                      }}
                      className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-medium text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleExportPrivateKey}
                      disabled={exportReason.length < 10 || processing}
                      className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium disabled:opacity-50 transition-colors"
                    >
                      {processing ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Export Key'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  <div className="bg-slate-900 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-400 text-sm">Private Key</span>
                      <button
                        onClick={() => copyToClipboard(exportedKey)}
                        className="text-slate-400 hover:text-white transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-white font-mono text-xs break-all">
                      {exportedKey}
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setShowExportModal(false)
                      setExportedKey(null)
                      setExportReason('')
                      setSelectedAddress(null)
                    }}
                    className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
